/**
 * Centralized Polling Configuration
 *
 * This module provides polling configuration with different presets for various use cases:
 * - FLOW_LOGS: Real-time log viewing (fast updates)
 * - LIVE_SAMPLES: Checking for initial data arrival (moderate)
 * - PIPELINE_LIST: Dashboard list refresh (slower, less aggressive)
 * - UI_TIME_DISPLAY: Client-side time display updates
 * - SCANNING: Fast polling for detection workflows
 * - DEFAULT: General purpose polling
 *
 * Configuration priority (highest to lowest):
 * - GrowthBook feature flag (if intervalMs is set for preset)
 * - Preset-specific default value
 * - Environment variable REACT_APP_POLLING_INTERVAL_MS (for DEFAULT preset)
 * - Hardcoded default (10000ms / 10 seconds)
 */

import { POLLING_INTERVAL_MS } from 'js/constants/config';
import { PollingConfig } from './featureFlags';

/**
 * Minimum allowed polling interval (1 second)
 * Prevents accidental DoS on backend
 */
export const MIN_POLLING_INTERVAL_MS = 1000;

/**
 * Maximum allowed polling interval (5 minutes)
 * Prevents UX issues from excessively long polling intervals
 */
export const MAX_POLLING_INTERVAL_MS = 300000;

/**
 * Polling preset identifiers for different use cases
 */
export type PollingPreset =
  | 'DEFAULT'
  | 'FLOW_LOGS'
  | 'LIVE_SAMPLES'
  | 'PIPELINE_LIST'
  | 'UI_TIME_DISPLAY'
  | 'SCANNING';

/**
 * Default intervals for each preset (in milliseconds)
 * These can be overridden via GrowthBook feature flag
 */
export const POLLING_PRESET_INTERVALS: Record<PollingPreset, number> = {
  /** General purpose polling - uses env variable or 10s default */
  DEFAULT: POLLING_INTERVAL_MS,
  /** Flow run logs - real-time log viewing needs fast updates (10s) */
  FLOW_LOGS: 10000,
  /** Live samples polling - checking for initial data arrival (20s) */
  LIVE_SAMPLES: 20000,
  /** Pipeline list refresh - dashboard doesn't need aggressive polling (30s) */
  PIPELINE_LIST: 30000,
  /** UI time display - client-side only, no API calls (10s) */
  UI_TIME_DISPLAY: 10000,
  /** Scanning mode - fast polling for detection workflows (5s) */
  SCANNING: 5000,
};

/**
 * Base polling configuration shared across all presets
 */
const BASE_POLLING_CONFIG: Omit<PollingConfig, 'intervalMs' | 'presets'> = {
  pauseOnHidden: true,
  refreshOnVisible: true,
  pauseOnIdle: false,
  idleTimeoutMs: 300000, // 5 minutes
  enableAdaptiveBackoff: false,
  maxBackoffMultiplier: 4,
};

/**
 * Preset-specific configuration overrides
 * Allows different presets to have different behavior (e.g., pauseOnIdle)
 */
export const POLLING_PRESET_CONFIGS: Record<PollingPreset, Partial<PollingConfig>> = {
  DEFAULT: {},
  FLOW_LOGS: {
    // Logs should refresh immediately when tab becomes visible
    refreshOnVisible: true,
  },
  LIVE_SAMPLES: {
    // Live samples can pause when idle since it's a background check
    pauseOnIdle: true,
  },
  PIPELINE_LIST: {
    // Pipeline list can have adaptive backoff to reduce load when unchanged
    enableAdaptiveBackoff: true,
    maxBackoffMultiplier: 2,
  },
  UI_TIME_DISPLAY: {
    // UI display doesn't need to pause (no API calls)
    pauseOnHidden: false,
    pauseOnIdle: false,
  },
  SCANNING: {
    // Scanning needs fast, continuous polling
    pauseOnHidden: false,
    refreshOnVisible: true,
  },
};

/**
 * Default polling configuration (for backward compatibility)
 * Uses environment variable for intervalMs, with sensible defaults for other options
 */
export const DEFAULT_POLLING_CONFIG: PollingConfig = {
  ...BASE_POLLING_CONFIG,
  intervalMs: POLLING_INTERVAL_MS,
};

/**
 * Resolved polling config type with non-null intervalMs
 * Used after merging with defaults (excludes presets field)
 */
export interface ResolvedPollingConfig extends Omit<PollingConfig, 'intervalMs' | 'presets'> {
  intervalMs: number;
  /** The preset this config was derived from */
  preset?: PollingPreset;
}

/**
 * Validate and clamp polling interval to safe bounds
 *
 * @param intervalMs - The interval to validate
 * @param defaultValue - Fallback value if interval is invalid
 * @returns A valid polling interval within bounds
 */
function validatePollingInterval(
  intervalMs: number | null | undefined,
  defaultValue: number,
): number {
  // Use default for null/undefined
  if (intervalMs == null) {
    return defaultValue;
  }

  // Use default for non-numbers or NaN
  if (typeof intervalMs !== 'number' || isNaN(intervalMs)) {
    console.warn(
      `[PollingConfig] Invalid polling interval: ${intervalMs}, using default ${defaultValue}ms`,
    );
    return defaultValue;
  }

  // Clamp to valid range
  if (intervalMs < MIN_POLLING_INTERVAL_MS) {
    console.warn(
      `[PollingConfig] Polling interval ${intervalMs}ms is below minimum (${MIN_POLLING_INTERVAL_MS}ms), clamping to minimum`,
    );
    return MIN_POLLING_INTERVAL_MS;
  }

  if (intervalMs > MAX_POLLING_INTERVAL_MS) {
    console.warn(
      `[PollingConfig] Polling interval ${intervalMs}ms exceeds maximum (${MAX_POLLING_INTERVAL_MS}ms), clamping to maximum`,
    );
    return MAX_POLLING_INTERVAL_MS;
  }

  return intervalMs;
}

/**
 * Get the default configuration for a specific preset
 */
export function getPresetConfig(preset: PollingPreset): ResolvedPollingConfig {
  return {
    ...BASE_POLLING_CONFIG,
    ...POLLING_PRESET_CONFIGS[preset],
    intervalMs: validatePollingInterval(POLLING_PRESET_INTERVALS[preset], 10000),
    preset,
  };
}

/**
 * Merge GrowthBook config with preset defaults
 *
 * Configuration priority (highest to lowest):
 * 1. GrowthBook preset-specific override (gbConfig.presets[preset])
 * 2. GrowthBook global override (gbConfig.intervalMs, etc.)
 * 3. Hardcoded preset defaults (POLLING_PRESET_CONFIGS, POLLING_PRESET_INTERVALS)
 * 4. Base config defaults (BASE_POLLING_CONFIG)
 *
 * Example GrowthBook JSON:
 * ```json
 * {
 *   "intervalMs": null,
 *   "pauseOnHidden": true,
 *   "presets": {
 *     "FLOW_LOGS": { "intervalMs": 15000 },
 *     "PIPELINE_LIST": { "intervalMs": 45000, "pauseOnIdle": true },
 *     "SCANNING": { "intervalMs": 3000 }
 *   }
 * }
 * ```
 *
 * @param gbConfig - Configuration from GrowthBook feature flag
 * @param preset - The polling preset to use (defaults to 'DEFAULT')
 * @returns Resolved configuration with validated values
 */
export function mergePollingConfig(
  gbConfig: Partial<PollingConfig> | null | undefined,
  preset: PollingPreset = 'DEFAULT',
): ResolvedPollingConfig {
  // Step 1: Start with hardcoded preset config
  const presetConfig = getPresetConfig(preset);

  if (!gbConfig) {
    return presetConfig;
  }

  // Step 2: Extract global and preset-specific configs from GrowthBook
  const { presets: gbPresets, ...gbGlobalConfig } = gbConfig;

  // Step 3: Get preset-specific override from GrowthBook (if any)
  const gbPresetConfig = gbPresets?.[preset] ?? {};

  // Step 4: Merge in order: preset defaults <- global GB <- preset-specific GB
  // For intervalMs, we need special handling to determine the final value
  const globalIntervalMs = gbGlobalConfig.intervalMs;
  const presetIntervalMs = gbPresetConfig.intervalMs;

  // Determine final intervalMs:
  // - Preset-specific GB override takes highest priority
  // - Then global GB override
  // - Then hardcoded preset default
  const finalIntervalMs = presetIntervalMs ?? globalIntervalMs ?? presetConfig.intervalMs;

  // Build the merged config
  const mergedConfig: ResolvedPollingConfig = {
    ...presetConfig, // Base: hardcoded preset defaults
    ...gbGlobalConfig, // Override with global GrowthBook settings
    ...gbPresetConfig, // Override with preset-specific GrowthBook settings
    // Validate and clamp intervalMs to safe bounds
    intervalMs: validatePollingInterval(finalIntervalMs, presetConfig.intervalMs),
    preset,
  };

  // Remove the presets field from the final config (it's not needed after merge)
  delete (mergedConfig as any).presets;

  return mergedConfig;
}

/**
 * Get the resolved interval for a preset from GrowthBook config
 * Useful for class components that can't use hooks
 *
 * @param gbConfig - Configuration from GrowthBook feature flag
 * @param preset - The polling preset to get interval for
 * @returns The resolved interval in milliseconds
 */
export function getPresetInterval(
  gbConfig: Partial<PollingConfig> | null | undefined,
  preset: PollingPreset,
): number {
  const resolved = mergePollingConfig(gbConfig, preset);
  return resolved.intervalMs;
}
